import { useCallback, useMemo } from 'react'
import { getDocIdAsString } from '../../utils/documents'

/**
 * Navigation helpers for payroll workers lists.
 *
 * This hook encapsulates the navigation state derived from a list of workers and
 * an optional filter. It exposes the navigable list, current index, booleans for
 * previous/next availability, convenience navigation helpers that return target ids,
 * and the resolved current id when the given one is not present in the navigable list.
 *
 * Invariants:
 * - The list is assumed to contain unique document ids.
 * - All returned ids are strings via `getDocIdAsString`.
 */
export interface IdentifiableEntity {
  _id: unknown
}

export interface UsePayrollWorkersNavigationResult<T extends IdentifiableEntity> {
  navigableWorkers: T[]
  currentIndex: number
  hasPrev: boolean
  hasNext: boolean
  currentWorker: T | undefined
  currentNavigableWorker: T | undefined
  resolvedCurrentWorkerId: string | undefined
  goPrev: () => string | undefined
  goNext: () => string | undefined
}

export function usePayrollWorkersNavigation<T extends IdentifiableEntity>(
  workers: T[],
  currentWorkerId: string | undefined,
): UsePayrollWorkersNavigationResult<T> {
  // Build the navigable subset according to the provided filter
  const navigableWorkers = workers
  // Compute the current index within the navigable subset
  const currentIndex = useMemo(() => {
    if (!currentWorkerId) return -1
    return navigableWorkers.findIndex(w => getDocIdAsString(w) === currentWorkerId)
  }, [currentWorkerId, navigableWorkers])

  // Navigation availability when the current item is not part of the navigable list
  const hasPrev = currentIndex > 0 || (currentIndex === -1 && navigableWorkers.length > 0)
  const hasNext =
    (currentIndex >= 0 && currentIndex < navigableWorkers.length - 1) ||
    (currentIndex === -1 && navigableWorkers.length > 1)

  // Compute navigation targets (pure, no side-effects)
  const goPrev = useCallback((): string | undefined => {
    if (!hasPrev) return undefined
    if (currentIndex === -1) {
      return navigableWorkers[0] ? getDocIdAsString(navigableWorkers[0]) : undefined
    }
    return getDocIdAsString(navigableWorkers[currentIndex - 1])
  }, [hasPrev, currentIndex, navigableWorkers])

  const goNext = useCallback((): string | undefined => {
    if (!hasNext) return undefined
    if (currentIndex === -1) {
      if (navigableWorkers[1]) return getDocIdAsString(navigableWorkers[1])
      return navigableWorkers[0] ? getDocIdAsString(navigableWorkers[0]) : undefined
    }
    return getDocIdAsString(navigableWorkers[currentIndex + 1])
  }, [hasNext, currentIndex, navigableWorkers])

  // Resolve current worker and navigable worker references
  const currentWorker = useMemo(() => {
    if (!currentWorkerId) return undefined
    return workers.find(w => getDocIdAsString(w) === currentWorkerId)
  }, [currentWorkerId, workers])

  const currentNavigableWorker = useMemo(() => {
    if (!currentWorkerId) return undefined
    return navigableWorkers.find(w => getDocIdAsString(w) === currentWorkerId)
  }, [currentWorkerId, navigableWorkers])

  // Resolve a safe current id within the navigable subset when needed
  const resolvedCurrentWorkerId = useMemo(() => {
    if (!currentWorkerId && navigableWorkers.length > 0) {
      return getDocIdAsString(navigableWorkers[0])
    }
    if (currentWorkerId) {
      const stillExists = navigableWorkers.some(w => getDocIdAsString(w) === currentWorkerId)
      if (!stillExists && navigableWorkers.length > 0) {
        const boundedIndex = Math.min(Math.max(currentIndex, 0), navigableWorkers.length - 1)
        return getDocIdAsString(navigableWorkers[boundedIndex])
      }
    }
    return undefined
  }, [currentWorkerId, navigableWorkers, currentIndex])

  return {
    navigableWorkers,
    currentIndex,
    hasPrev,
    hasNext,
    currentWorker,
    currentNavigableWorker,
    resolvedCurrentWorkerId,
    goPrev,
    goNext,
  }
}
